/**
 * The story studio (spec 025): the chat-first view of a story, for Maya.
 * Three areas side by side:
 *
 * - the chat: 024's story conversation (`StoryAssistantPanel`, the same
 *   component and conversation as the hood's Assistant tab), naming the
 *   scene it talks about and sending it as `selectedSceneId`;
 * - the result: the selected scene playing in spec 009's `PlayerHost`, the
 *   direct-edit strip (D-4) and "Play the story" (D-8); right after a
 *   brief's draft, spec 026's handoff in place of the preview;
 * - the map: the story's route as a Path or a Fly-through (`V` toggles).
 *
 * The studio adds a view, not a format: it edits through the scene store,
 * the story operations (023) and the assistant (024), writes no node
 * positions, and changes no runtime code.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChoiceOption, Orientation, StoryApiResponse, StoryPackageManifest, Story } from '../../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../../Shared/src/runtime/playableScene';
import { cacheDraft, previewSceneInput, useSceneStore } from '../../stores/sceneStore';
import { useStoryStore } from '../../stores/storyStore';
import { storyKey, useAssistantStore, type AssistantMessage } from '../../stores/assistantStore';
import { isEditingTarget, isModalOpen } from '../../hooks/useSceneShortcuts';
import { buildStoryRoute, type RouteNode } from '../../utils/storyRoute';
import { openingMapLook, writeMapLook, type MapLook } from '../../utils/storyView';
import { loadStoryPreviewScenes } from '../../utils/previewSources';
import { buildPreviewManifest } from '../../utils/previewRunner';
import { PlayerHost, type PlayerHostHandle } from '../preview/PlayerHost';
import StoryRunnerPreview, { type RunnerPosition } from '../preview/StoryRunnerPreview';
import OrientationButtons from '../preview/OrientationButtons';
import StoryAssistantPanel from '../assistant/StoryAssistantPanel';
import { momentOf, useAssistantView } from '../assistant/assistantView';
import StoryMap from './StoryMap';
import { studioBadges } from './studioBadges';
import FlyThrough, { type FrameDriver } from './FlyThrough';
import SceneCard from './SceneCard';
import DirectEdits from './DirectEdits';
import PicturesHandoff, { closeHandoff, isHandoffClosed } from '../pictures/PicturesHandoff';
import { useOpenPictures } from '../pictures/usePicturesNav';
import { pictureMapCounts } from '../../../../Shared/src/pictures';
import { useSceneThumbs } from './sceneThumbs';
import styles from './StoryStudio.module.scss';

export const STUDIO_TEXT = {
  empty: "Your story will appear here. Tell the assistant what it's about.",
  noEdge: "This choice doesn't lead anywhere yet.",
  storyEnds: 'The story ends here.',
  unavailableHelp: 'You can still play your story, fix lines and pictures here, or build it by hand under the hood.',
  saveFailed: "Your changes couldn't be saved yet.",
} as const;

export interface StoryStudioProps {
  story: StoryApiResponse;
  /** The selected scene's graph node (shared with the hood's map). */
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  /** Switch to Under the hood. */
  onOpenHood: () => void;
  userId: number;
  /** Tests: the fly-through's frame driver. */
  frames?: FrameDriver;
  reducedMotion?: boolean;
}

const EMPTY_MESSAGES: AssistantMessage[] = [];

/** The scene store's scene as the preview plays it, when it is `sceneId`. */
function usePreviewScene(sceneId: number | null): PlayableSceneInput | null {
  const scene = useSceneStore((s) => s.scene);
  const keyframes = useSceneStore((s) => s.keyframes);
  const isLoading = useSceneStore((s) => s.isLoading);
  return useMemo(
    () => (sceneId !== null ? (previewSceneInput(sceneId) as unknown as PlayableSceneInput | null) : null),
    // scene, keyframes and isLoading are what previewSceneInput reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sceneId, scene, keyframes, isLoading],
  );
}

/** The selected scene playing from 0, held at the choice reveal (009). */
const StudioPreview: React.FC<{
  sceneId: number;
  scene: PlayableSceneInput | null;
  orientation: Orientation;
  onChoice: (choice: ChoiceOption) => void;
  /** The preview's handle, read by the studio's assistant view (spec 031 v3). */
  hostRef: React.RefObject<PlayerHostHandle>;
}> = ({ sceneId, scene, orientation, onChoice, hostRef }) => {
  const [paused, setPaused] = useState(false);
  useEffect(() => { setPaused(false); }, [sceneId]);
  return (
    <div className={styles.stage} data-testid="studio-preview">
      {scene ? (
        <PlayerHost
          key={sceneId}
          ref={hostRef}
          scene={scene}
          orientation={orientation}
          playing
          onChoice={onChoice}
          className={styles.player}
          data-testid="studio-player"
        />
      ) : (
        <div className={styles.stageLoading}><div className={styles.spinner} /></div>
      )}
      {scene && (
        <div className={styles.stageControls}>
          {paused ? (
            <button type="button" className={styles.iconButton} aria-label="Play" title="Play" onClick={() => { hostRef.current?.resume(); setPaused(false); }}>▶</button>
          ) : (
            <button type="button" className={styles.iconButton} aria-label="Pause" title="Pause" onClick={() => { hostRef.current?.pause(); setPaused(true); }}>⏸</button>
          )}
          <button type="button" className={styles.iconButton} aria-label="Play from the start" title="Play from the start" onClick={() => { hostRef.current?.play(0); setPaused(false); }}>↺</button>
        </div>
      )}
    </div>
  );
};

export const StoryStudio: React.FC<StoryStudioProps> = ({
  story, selectedNodeId, onSelectNode, onOpenHood, userId, frames, reducedMotion,
}) => {
  const graph = story.graph;
  const sceneChoicesMap = useStoryStore((s) => s.sceneChoicesMap);
  const runnerActive = useStoryStore((s) => s.previewRunnerActive);
  const setRunnerActive = useStoryStore((s) => s.setPreviewRunnerActive);
  const syncChoiceEdges = useStoryStore((s) => s.syncChoiceEdges);
  const messages = useAssistantStore((s) => s.conversations[storyKey(story.id)] ?? EMPTY_MESSAGES);
  const pictureMap = useStoryStore((s) => s.pictureMap);
  const openPictures = useOpenPictures();

  // ── The route and the cards ──────────────────────────────────────────────
  const route = useMemo(() => buildStoryRoute(graph, story.storyScenes), [graph, story.storyScenes]);
  const sceneNodeIds = useMemo(() => new Set(graph.nodes.filter((n) => n.type !== 'end').map((n) => n.id)), [graph.nodes]);

  // ── The selected scene ───────────────────────────────────────────────────
  // At most one, always a scene on the graph: else the start scene, else none.
  useEffect(() => {
    if (selectedNodeId && sceneNodeIds.has(selectedNodeId)) return;
    const start = sceneNodeIds.has(graph.startNodeId) ? graph.startNodeId : null;
    if (start !== selectedNodeId) onSelectNode(start);
  }, [selectedNodeId, sceneNodeIds, graph.startNodeId, onSelectNode]);

  const selectedNode = selectedNodeId && sceneNodeIds.has(selectedNodeId)
    ? graph.nodes.find((n) => n.id === selectedNodeId) ?? null
    : null;
  const selectedRow = selectedNode ? story.storyScenes.find((ss) => ss.id === selectedNode.storySceneId) ?? null : null;
  const selectedSceneId = selectedRow?.sceneId ?? null;
  const selectedName = selectedRow ? selectedRow.sceneName || `Scene #${selectedRow.sceneId}` : null;

  // Choosing a scene saves the one before, then loads it into the scene store
  // (as a scene tab does), so its edits, autosave and undo are the scene editor's.
  const [saveFailed, setSaveFailed] = useState(false);
  useEffect(() => {
    if (selectedSceneId === null) return;
    let cancelled = false;
    void (async () => {
      const sc = useSceneStore.getState();
      if (sc.scene && Number(sc.scene.id) !== selectedSceneId && sc.isDirty) {
        cacheDraft();
        await sc.saveScene();
        setSaveFailed(useSceneStore.getState().saveStatus === 'error');
      }
      if (cancelled) return;
      const now = useSceneStore.getState();
      if (Number(now.scene?.id) !== selectedSceneId) {
        await now.loadScene(selectedSceneId);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSceneId]);

  // Leaving the studio never drops work (as the scene editor's unmount, 004).
  useEffect(() => () => {
    const st = useSceneStore.getState();
    if (st.scene && st.isDirty) {
      cacheDraft();
      void st.saveScene();
    }
  }, []);

  // The selected scene's choices feed the story's listing on load and save (as the hood's bridge).
  const selectedStorySceneId = selectedRow?.id ?? null;
  useEffect(() => {
    if (selectedSceneId === null || selectedStorySceneId === null) return undefined;
    const sync = () => {
      const st = useSceneStore.getState();
      if (Number(st.scene?.id) !== selectedSceneId || st.isLoading) return;
      syncChoiceEdges(selectedStorySceneId, st.scene?.choices ?? []);
    };
    const offLoad = useSceneStore.subscribe((s) => s.isLoading, (loading, prev) => { if (prev && !loading) sync(); });
    const offSave = useSceneStore.subscribe((s) => s.isDirty, (dirty, prev) => { if (prev && !dirty) sync(); });
    return () => { offLoad(); offSave(); };
  }, [selectedSceneId, selectedStorySceneId, syncChoiceEdges]);

  const previewScene = usePreviewScene(selectedSceneId);
  const liveScene = useSceneStore((s) => (selectedSceneId !== null && Number(s.scene?.id) === selectedSceneId && !s.isLoading ? s.scene : null)) as Story | null;

  // ── Badges (D-6) ─────────────────────────────────────────────────────────
  const { badges, storyWideMissing } = useMemo(
    () => studioBadges(story, sceneChoicesMap, messages, pictureMap),
    [story, sceneChoicesMap, messages, pictureMap],
  );

  // ── Thumbnails ───────────────────────────────────────────────────────────
  const pictureIds = useMemo(
    () => (liveScene?.sprites ?? []).flatMap((sp) => sp.states.map((st) => st.mediaId).filter((id): id is number => !!id)),
    [liveScene?.sprites],
  );
  const { thumbs, srcFor } = useSceneThumbs(story, pictureIds);

  // ── The map's look ───────────────────────────────────────────────────────
  const [look, setLookState] = useState<MapLook>(() => openingMapLook(userId, sceneNodeIds.size));
  const setLook = useCallback((next: MapLook) => {
    setLookState(next);
    writeMapLook(userId, next);
  }, [userId]);
  const lookRef = useRef(look);
  lookRef.current = look;

  // ── Keys: V toggles the look; Cmd/Ctrl+Z undoes the scene (result area) or the story ─
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target) || isModalOpen()) return;
      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();
      if (key === 'v' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setLook(lookRef.current === 'path' ? 'fly' : 'path');
        return;
      }
      const undo = cmd && key === 'z' && !e.shiftKey;
      const redo = (cmd && key === 'z' && e.shiftKey) || (e.ctrlKey && key === 'y');
      if (!undo && !redo) return;
      e.preventDefault();
      const inResult = e.target instanceof Node && !!resultRef.current?.contains(e.target);
      if (inResult) {
        const sc = useSceneStore.getState();
        if (sc.isPlaying) return;
        if (undo) sc.undo();
        else sc.redo();
        return;
      }
      const st = useStoryStore.getState();
      if (st.previewRunnerActive) return;
      void (undo ? st.undo() : st.redo());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setLook]);

  // ── The preview's choices walk the story ─────────────────────────────────
  const [choiceNote, setChoiceNote] = useState<string | null>(null);
  useEffect(() => { setChoiceNote(null); }, [selectedNodeId]);
  const handleChoice = useCallback((choice: ChoiceOption) => {
    if (!selectedNodeId) return;
    const edge = graph.edges.find((e) => e.from === selectedNodeId && e.condition === choice.id);
    const target = edge ? graph.nodes.find((n) => n.id === edge.to) : undefined;
    if (!target) {
      setChoiceNote(STUDIO_TEXT.noEdge);
      return;
    }
    if (target.type === 'end') {
      setChoiceNote(STUDIO_TEXT.storyEnds);
      return;
    }
    onSelectNode(target.id);
  }, [graph, selectedNodeId, onSelectNode]);

  // ── Play the story (D-8) ─────────────────────────────────────────────────
  const [orientation, setOrientation] = useState<Orientation>('landscape');
  // Spec 031 v3: what the author sees here, read at Send: the studio's orientation,
  // and the selected scene's moment while its preview is shown (D-16).
  const hostRef = useRef<PlayerHostHandle>(null);
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;
  useAssistantView(true, () => {
    const host = hostRef.current;
    const atMs = momentOf(host?.getFrameState()?.t);
    return {
      surface: 'studio',
      orientation: orientationRef.current,
      ...(host && atMs !== undefined ? { atMs, playing: host.isPlaying() } : {}),
    };
  });
  const [manifest, setManifest] = useState<StoryPackageManifest | null>(null);
  useEffect(() => {
    if (!runnerActive) { setManifest(null); return undefined; }
    let cancelled = false;
    void loadStoryPreviewScenes(story).then((scenes) => {
      if (!cancelled) setManifest(buildPreviewManifest(story, scenes));
    });
    return () => { cancelled = true; };
    // The runner plays the story as it was when it started.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runnerActive]);
  // The maps follow the playing story (D-10): the scene the runner plays
  // becomes the selected scene, by the same path as a card click.
  const followRef = useRef({ selectedNodeId, sceneNodeIds, onSelectNode });
  followRef.current = { selectedNodeId, sceneNodeIds, onSelectNode };
  const handleRunnerPosition = useCallback((position: RunnerPosition) => {
    const f = followRef.current;
    if (!position.nodeId || !f.sceneNodeIds.has(position.nodeId) || position.nodeId === f.selectedNodeId) return;
    f.onSelectNode(position.nodeId);
  }, []);

  // Leaving the studio stops the runner.
  useEffect(() => () => { useStoryStore.getState().setPreviewRunnerActive(false); }, []);

  const renderFlyCard = useCallback((n: RouteNode) => {
    const b = badges.get(n.id) ?? {};
    const thumb = n.kind === 'scene' ? thumbs.get(n.storySceneId) : undefined;
    return (
      <SceneCard
        kind={n.kind}
        name={n.name}
        stop={n.stop}
        thumb={thumb}
        thumbSrc={thumb && thumb.kind !== 'tile' ? srcFor(thumb.mediaId) : undefined}
        isStart={n.id === graph.startNodeId}
        selected={n.id === selectedNodeId}
        missingMedia={b.missingMedia}
        needsFix={b.needsFix}
      />
    );
  }, [badges, thumbs, srcFor, graph.startNodeId, selectedNodeId]);

  // ── After the brief: the handoff (spec 026 D-10) ──────────────────────────
  // While the latest applied turn is the one that answered a pending brief
  // and the map lists a picture, until it is closed (remembered per story
  // for the page session).
  const [handoffClosed, setHandoffClosed] = useState(() => isHandoffClosed(story.id));
  useEffect(() => { setHandoffClosed(isHandoffClosed(story.id)); }, [story.id]);
  const lastTurn = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && m.turnId !== undefined) return m;
    }
    return undefined;
  }, [messages]);
  const showHandoff = !handoffClosed && !!lastTurn?.fromBrief && pictureMapCounts(pictureMap).total > 0;
  const endHandoff = useCallback(() => {
    closeHandoff(story.id);
    setHandoffClosed(true);
  }, [story.id]);

  const hoodLink = (
    <button type="button" className={styles.linkButton} onClick={onOpenHood} data-testid="studio-retry-hood">
      or open Under the hood
    </button>
  );
  const hasScenes = sceneNodeIds.size > 0;
  // Spec 028: a Changes card's Open selects that scene's node, so the stage shows it.
  const openChangedScene = useCallback((storySceneId: number) => {
    const node = graph.nodes.find((n) => n.type !== 'end' && n.storySceneId === storySceneId);
    if (node) onSelectNode(node.id);
  }, [graph, onSelectNode]);

  return (
    <div className={styles.studio} data-testid="story-studio">
      {/* The chat */}
      <section className={styles.chat} aria-label="Chat">
        {selectedName && (
          <div className={styles.talkingAbout} data-testid="studio-talking-about">Talking about: {selectedName}</div>
        )}
        <div className={styles.chatBody}>
          <StoryAssistantPanel
            storyId={story.id}
            selectedSceneId={selectedRow?.id}
            onOpenScene={openChangedScene}
            retryExtra={hoodLink}
            unavailableExtra={(
              <div className={styles.unavailableHelp} data-testid="studio-unavailable-help">
                <p>{STUDIO_TEXT.unavailableHelp}</p>
                <button type="button" className={styles.primaryButton} onClick={onOpenHood} data-testid="studio-unavailable-hood">
                  Open Under the hood
                </button>
              </div>
            )}
          />
        </div>
      </section>

      {/* The result */}
      <section className={styles.result} ref={resultRef} aria-label="Preview" tabIndex={-1}>
        <div className={styles.resultBar}>
          {runnerActive ? (
            <button type="button" className={styles.primaryButton} onClick={() => setRunnerActive(false)} data-testid="studio-stop-story">
              ■ Stop
            </button>
          ) : (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setRunnerActive(true)}
              disabled={!hasScenes || !sceneNodeIds.has(graph.startNodeId)}
              data-testid="studio-play-story"
            >
              ▶ Play the story
            </button>
          )}
          <OrientationButtons orientation={orientation} onChange={setOrientation} testIdPrefix="studio-orientation" />
        </div>

        {runnerActive ? (
          <div className={styles.stage} data-testid="studio-runner">
            {manifest ? (
              <StoryRunnerPreview
                story={story}
                manifest={manifest}
                orientation={orientation}
                onStop={() => setRunnerActive(false)}
                onPosition={handleRunnerPosition}
                wrapClassName={styles.runnerWrap}
              />
            ) : (
              <div className={styles.stageLoading}><div className={styles.spinner} /></div>
            )}
          </div>
        ) : !hasScenes ? (
          <div className={styles.emptyResult} data-testid="studio-empty">{STUDIO_TEXT.empty}</div>
        ) : showHandoff ? (
          <PicturesHandoff
            story={story}
            map={pictureMap}
            onFill={() => { endHandoff(); openPictures({ filter: 'needed' }); }}
            onClose={endHandoff}
          />
        ) : selectedSceneId !== null ? (
          <StudioPreview sceneId={selectedSceneId} scene={previewScene} orientation={orientation} onChoice={handleChoice} hostRef={hostRef} />
        ) : null}

        {choiceNote && !runnerActive && (
          <p className={styles.choiceNote} role="status" data-testid="studio-choice-note">{choiceNote}</p>
        )}
        {saveFailed && (
          <p className={styles.saveFailed} role="alert">{STUDIO_TEXT.saveFailed}</p>
        )}

        {liveScene && hasScenes && (
          <DirectEdits
            scene={liveScene}
            sceneKey={selectedRow ? `ss-${selectedRow.id}` : undefined}
            disabled={runnerActive}
            shared={selectedRow?.mode === 'reference'}
            srcFor={srcFor}
            onOpenHood={onOpenHood}
          />
        )}
      </section>

      {/* The map */}
      <section className={styles.map} aria-label="Map">
        <div className={styles.mapBar}>
          <div className={styles.lookSwitch} role="group" aria-label="Map look">
            <button
              type="button"
              className={`${styles.toggle} ${look === 'path' ? styles.toggleOn : ''}`}
              aria-pressed={look === 'path'}
              onClick={() => setLook('path')}
              data-testid="map-look-path"
            >
              Path
            </button>
            <button
              type="button"
              className={`${styles.toggle} ${look === 'fly' ? styles.toggleOn : ''}`}
              aria-pressed={look === 'fly'}
              onClick={() => setLook('fly')}
              data-testid="map-look-fly"
            >
              Fly-through
            </button>
          </div>
          {storyWideMissing > 0 && (
            <span className={styles.badgeMissing} data-testid="map-missing-chip">Missing media</span>
          )}
        </div>
        <div className={styles.mapBody} tabIndex={-1} data-testid="studio-map">
          {look === 'path' ? (
            <StoryMap
              reducedMotion={reducedMotion}
              route={route}
              startNodeId={graph.startNodeId}
              selectedNodeId={selectedNodeId}
              onSelect={onSelectNode}
              thumbs={thumbs}
              srcFor={srcFor}
              badges={badges}
            />
          ) : (
            <FlyThrough
              route={route}
              selectedNodeId={selectedNodeId}
              onSelect={onSelectNode}
              renderCard={renderFlyCard}
              frames={frames}
              reducedMotion={reducedMotion}
              storyId={story.id}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default StoryStudio;
